import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const requestIdRef = useRef(0);
  const didInitRef = useRef(false);

  const fetchMe = useCallback(async () => {
    const id = ++requestIdRef.current;
    try {
      const { data } = await api.get("/auth/me");
      if (requestIdRef.current !== id) return;
      setUser(data.user);
      setOrganization(data.organization);
    } catch {
      if (requestIdRef.current !== id) return;
      setUser(false);
      setOrganization(null);
    }
  }, []);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    requestIdRef.current++;
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    setOrganization(data.organization);
  };

  const loginWithCertificate = async (certificate_id) => {
    requestIdRef.current++;
    const { data } = await api.post("/auth/login-certificate", { certificate_id });
    setUser(data.user);
    setOrganization(data.organization);
  };

  const demoLogin = async () => {
    requestIdRef.current++;
    const { data } = await api.post("/auth/demo-login");
    setUser(data.user);
    setOrganization(data.organization);
  };

  const register = async (payload) => {
    requestIdRef.current++;
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    setOrganization(data.organization);
  };

  const logout = async () => {
    requestIdRef.current++;
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
      setOrganization(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, organization, login, loginWithCertificate, demoLogin, register, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
